<template>
  <v-dialog
    :key="currentIndex"
    v-model="dialog"
    max-width="500"
    persistent
  >
    <template #activator="{ props }">
      <v-btn
        :disabled="!enableAddNew"
        size="small"
        rounded
        class="primary-inverted-button"
        v-bind="props"
      >
        <v-icon
          tag="span"
          start
        >
          mdi-plus
        </v-icon>
        {{ $t("drivingPanelStrings.labels.addButton") }}
      </v-btn>
    </template>

    <v-card>
      <v-card-title>
        {{ formTitle }}
      </v-card-title>
      <v-card-text>
        <vee-form ref="tripsObserver">
          <v-container>
            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <claim-date-picker
                  :key="currentIndex"
                  :claim="item"
                  :label="$t('drivingPanelStrings.tableHeaders.date')"
                  :allowed-dates="
                    (x) => m(x).isSameOrBefore(m(), 'days')
                  "
                  @claim-date-change="updateTripAmount(item)"
                />
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <claim-type-picker
                  :claim="item"
                  configuration-type-property="rateType"
                  :filter-enabled-types-func="filterEnabledVehicleTypes"
                  resources-property="enums.vehicleTypes"
                  model-binding-property="claimTypeId"
                  :key="currentIndex"
                  :validation-name="
                    $t('drivingPanelStrings.tableHeaders.vehicleType')
                  "
                  :label="$t('drivingPanelStrings.tableHeaders.vehicleType')"
                  @claim-type-change="updateTripAmount(item)"
                />
              </v-col>
              <v-col
                v-if="canUseSalaryFraction"
                cols="12"
              >
                <v-checkbox
                  :key="currentIndex"
                  v-model="item.usesSalaryFraction"
                  color="primary"
                  :label="$t('commonStrings.salaryFractionLabel')"
                  :disabled="!salaryFractionSplitAvailable"
                />
              </v-col>
              <v-col cols="12">
                <paying-orgs-picker
                  :claim="item"
                  :filter-enabled-types-func="filterEnabledVehicleTypes"
                  configuration-type-property="rateType"
                  :key="currentIndex"
                  claim-type-property="claimTypeId"
                  :validation-name="
                    $t('drivingPanelStrings.tableHeaders.payingOrgs')
                  "
                  :label="$t('drivingPanelStrings.tableHeaders.payingOrgs')"
                  @paying-orgs-change="updateTripAmount(item)"
                />
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <vee-field
                  v-slot="{ componentField, errors }"
                  v-model="item.from"
                  rules="required|min:3|max:60"
                  :name="$t('drivingPanelStrings.tableHeaders.from')"
                >
                  <v-text-field
                    v-bind="componentField"
                    :key="currentIndex"
                    :label="$t('drivingPanelStrings.tableHeaders.from')"
                    :error-messages="errors"
                    :hint="$t('drivingPanelStrings.hints.from')"
                    counter="60"
                    variant="outlined"
                    density="compact"
                  />
                </vee-field>
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <vee-field
                  v-slot="{ componentField, errors }"
                  v-model="item.to"
                  rules="required|min:3|max:60"
                  :name="$t('drivingPanelStrings.tableHeaders.to')"
                >
                  <v-text-field
                    v-bind="componentField"
                    :key="currentIndex"
                    :error-messages="errors"
                    :hint="$t('drivingPanelStrings.hints.to')"
                    counter="60"
                    :label="$t('drivingPanelStrings.tableHeaders.to')"
                    variant="outlined"
                    density="compact"
                  />
                </vee-field>
              </v-col>
              <v-col cols="12">
                <vee-field
                  v-slot="{ componentField, errors }"
                  v-model="item.kilometers"
                  rules="required|decimal:1|min_value:1"
                  :name="$t('drivingPanelStrings.validationLabels.tripLength')"
                >
                  <decimal-text-field
                    v-bind="componentField"
                    :key="currentIndex"
                    :error-messages="errors"
                    :hint="$t('drivingPanelStrings.hints.tripLength')"
                    :label="
                      $t('drivingPanelStrings.validationLabels.tripLength')
                    "
                    @input="updateTripAmount(item)"
                  />
                </vee-field>
              </v-col>
              <v-col cols="12">
                <vee-field
                  v-slot="{ componentField, errors }"
                  v-model="item.passengers"
                  rules="name_list:2,2|max_list_items:16|max_list_item_length:60"
                  :name="$t('drivingPanelStrings.validationLabels.passengers')"
                  :custom-messages="{
                    name_list: $t(
                      'drivingPanelStrings.customErrors.passengers.name_list'
                    ),
                  }"
                >
                  <v-combobox
                    v-bind="componentField"
                    :multiple="true"
                    :chips="true"
                    :closable-chips="true"
                    :key="currentIndex"
                    :error-messages="errors"
                    :hint="$t('drivingPanelStrings.hints.passengers')"
                    :delimiters="[';', ',']"
                    :append-icon="null"
                    :label="
                      $t('drivingPanelStrings.validationLabels.passengers')
                    "
                    variant="outlined"
                    density="compact"
                    @update:modelValue="updatePassengers(item)"
                  />
                </vee-field>
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <amount-label
                  :label="$t('drivingPanelStrings.tableHeaders.amount')"
                  :value="item.amount"
                />
              </v-col>
            </v-row>
          </v-container>
        </vee-form>
      </v-card-text>
      <v-card-actions class="pa-5 flex-column flex-sm-row">
        <v-spacer />
        <v-btn
          rounded
          class="primary-inverted-button mb-2 mb-sm-0"
          :block="xs"
          size="small"
          @click="cancel"
        >
          {{ $t("salaryPanelStrings.labels.cancelButton") }}
        </v-btn>
        <v-btn
          rounded
          class="primary-button ml-0 ml-sm-2"
          :block="xs"
          size="small"
          @click="save"
        >
          {{ saveLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ClaimDatePicker from "../components/ClaimDatePicker";
import ClaimTypePicker from "../components/ClaimTypePicker";
import PayingOrgsPicker from "../components/PayingOrgsPicker";
import { Form as VeeForm, Field as VeeField } from "vee-validate";
import validationErrors from "@/mixins/validationErrors";
import AmountLabel from "@/views/common/components/AmountLabel";
import DecimalTextField from "@/views/common/components/DecimalTextField";
import { mapGetters } from "vuex";
import { useDisplay } from 'vuetify';
import { m } from '@/plugins/moment';

export default {
  name: "TripEditDialog",
  components: {
    VeeForm,
    VeeField,
    ClaimDatePicker,
    ClaimTypePicker,
    PayingOrgsPicker,
    AmountLabel,
    DecimalTextField
  },
  mixins: [validationErrors],
  props: {
    item: {
      required: true
    },
    updateTripAmount: {
      required: true
    },
    currentIndex: {
      required: true
    },
    canUseSalaryFraction: {
      required: true
    },
    enableAddNew:{
      required: true,
      type: Boolean
    }
  },
  setup() {
    const { xs } = useDisplay();
    return { xs, m };
  },
  data() {
    return {
      dialog: false
    };
  },
  watch: {
    currentIndex(val) {
      if (val >= 0) {
        this.dialog = true;
      }
    },
    dialog(val) {
      if (val && this.currentIndex < 0 && this.$refs.tripsObserver) {
          this.$refs.tripsObserver.reset();
      }
    }
  },
  computed: {
    ...mapGetters({
      salaryFractionSplitAvailable:
        "application/salaryFractionsStore/salaryFractionSplitAvailable",
      frac: "application/salaryFractionsStore/fractions"
    }),
    saveLabel() {
      if (this.currentIndex < 0) {
        return this.$t("drivingPanelStrings.labels.insertButton");
      } else {
        return this.$t("drivingPanelStrings.labels.updateButton");
      }
    },
    formTitle() {
      if (this.currentIndex < 0) {
        return this.$t("drivingPanelStrings.labels.insertEditDialogTitle");
      } else {
        return this.$t("drivingPanelStrings.labels.updateEditDialogTitle");
      }
    }
  },
  methods: {
    updatePassengers(claim) {
      claim.passengers = claim.passengers.map(x =>
        x.trim().replace(/\s\s+/g, " ")
      );

      return this.updateTripAmount(claim);
    },
    filterEnabledVehicleTypes(conf) {
      return conf["vehicleRates"]["vehicleRateTypes"].filter(x => x.enabled);
    },
    close() {
      this.dialog = false;
    },
    cancel() {
      this.$emit("edit-cancelled");
      this.close();
    },
    async save() {
      const isDataValid = await this.$refs.tripsObserver.validate();

      if (!isDataValid) {
        this.showValidationErrorSnackbar(this.$refs.tripsObserver);
        return;
      }

      if (this.currentIndex >= 0) {
        this.$emit("item-changed", {
          claim: this.item,
          index: this.currentIndex
        });
      } else {
        this.$emit("new-item-added", this.item);
      }

      this.close();
    }
  }
};
</script>
